import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { CheckCircle2, XCircle, AlertCircle, Loader2, Activity } from "lucide-react";
import axios from "axios";
import { API_URL } from '../../config/api';

// Mask IP address for privacy
const maskIp = (ip) => {
  if (!ip) return 'Detected';
  const parts = ip.split('.');
  if (parts.length === 4) {
    return `${parts[0]}.${parts[1]}.${parts[2]}.xxx`;
  }
  return 'Detected';
};

// Format server time with timezone
const formatServerTime = (isoString) => {
  if (!isoString) return 'Unknown';
  const time = isoString.substring(11, 19);
  return `${time} UTC`;
};

export const PreFlightStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkSystemHealth();
    // Re-check every 30 seconds
    const interval = setInterval(checkSystemHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const checkSystemHealth = async () => {
    try {
      setError(null);
      const response = await axios.get(`${API_URL}/health/pre-flight`);
      
      setStatus(response.data);
      setLoading(false);
    } catch (err) {
      console.error("Pre-flight check error:", err);
      setError(err.message);
      setStatus({
        tests: [{
          name: "Backend Connection",
          status: "fail",
          message: `Cannot reach backend API: ${err.message}`
        }],
        preFlightPassed: false
      });
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="shadow-none border border-gray-300">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center gap-3 text-gray-600">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Checking system infrastructure...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const allPassed = status?.preFlightPassed;
  const borderColor = allPassed ? "border-green-500" : "border-red-500";
  const bgColor = allPassed ? "bg-green-50" : "bg-red-50";

  return (
    <Card className={`border ${borderColor} ${bgColor}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          {allPassed ? (
            <>
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <span className="text-green-900">System Infrastructure: Operational</span>
            </>
          ) : (
            <>
              <XCircle className="h-5 w-5 text-red-600" />
              <span className="text-red-900">System Infrastructure: Issues Detected</span>
            </>
          )}
          <div className="ml-auto flex items-center gap-2 text-xs text-gray-500">
            <Activity className="h-3 w-3" />
            Auto-refreshing
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Server Info Section */}
        {status?.serverInfo && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="font-medium text-blue-900">Backend:</span>
                <span className="ml-2 text-blue-700">{status.serverInfo.backendUrl?.split('//')[1] || 'Unknown'}</span>
              </div>
              <div>
                <span className="font-medium text-blue-900">Primary DB:</span>
                <span className="ml-2 text-green-700 font-bold">{status.serverInfo.primaryDb || status.serverInfo.postgresRegion || 'PostgreSQL'}</span>
              </div>
              <div>
                <span className="font-medium text-blue-900">Your IP:</span>
                <span className="ml-2 text-blue-700">{maskIp(status.serverInfo.clientIp)}</span>
              </div>
              <div>
                <span className="font-medium text-blue-900">Server Time:</span>
                <span className="ml-2 text-blue-700">{formatServerTime(status.serverInfo.serverTime)}</span>
              </div>
            </div>
            {/* Database Status Badges */}
            {status.databases && (
              <div className="mt-2 pt-2 border-t border-blue-200 flex gap-2">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  status.databases.postgresql?.status === 'connected' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  PostgreSQL: {status.databases.postgresql?.status || 'unknown'}
                </span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  status.databases.mongodb?.status === 'connected' 
                    ? 'bg-gray-100 text-gray-600' 
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  MongoDB: {status.databases.mongodb?.status || 'migrating'}
                </span>
              </div>
            )}
          </div>
        )}
        
        <div className="space-y-2">
          {status?.tests?.map((test, idx) => (
            <div key={idx} className="flex items-center justify-between py-2 px-3 bg-white rounded border">
              <div className="flex items-center gap-2">
                {test.status === 'pass' ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                ) : test.status === 'warning' ? (
                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-600" />
                )}
                <span className="text-sm font-medium">{test.name}</span>
              </div>
              <span className="text-xs text-gray-600">{test.message}</span>
            </div>
          ))}
        </div>
        
        {!allPassed && (
          <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded text-sm text-red-800">
            <strong>CRITICAL:</strong> Fix infrastructure issues before running tests!
          </div>
        )}
        
        {error && (
          <div className="mt-2 text-xs text-gray-500">
            Debug: {error}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
